import {gql} from '@apollo/client';

import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';

import {ProcedureRunStudyQuery, ProcedureRunStudyQueryVariables} from './__generated/ProcedureRunStudyQuery';

export const PROCEDURE_RUN_STUDY_QUERY = gql`
  query ProcedureRunStudyQuery($procedureRunId: StrictString!) @skipAudit @engComponent(name: STUDIES) {
    procedureRun(id: $procedureRunId) {
      ... on ProcedureRunNode {
        id
        study {
          ... on StudyNode {
            id
          }
          ... on InaccessibleInfoNode {
            invalidId
          }
        }
      }
      ... on InaccessibleInfoNode {
        invalidId
      }
    }
  }
`;

export enum ProcedureRunStudyIdStatus {
  SKIPPED = 'SKIPPED',
  PENDING = 'PENDING',
  ERROR = 'ERROR',
  PROCEDURE_RUN_INACCESSIBLE = 'PROCEDURE_RUN_INACCESSIBLE',
  STUDY_INACCESSIBLE = 'STUDY_INACCESSIBLE',
  SUCCESS = 'SUCCESS',
}

export interface SkippedProcedureRunStudyIdResult {
  status: ProcedureRunStudyIdStatus.SKIPPED;
}

export interface PendingProcedureRunStudyIdResult {
  status: ProcedureRunStudyIdStatus.PENDING;
}

export interface ErrorProcedureRunStudyIdResult {
  status: ProcedureRunStudyIdStatus.ERROR;
}

export interface ProcedureRunInaccessibleResult {
  status: ProcedureRunStudyIdStatus.PROCEDURE_RUN_INACCESSIBLE;
  invalidId: string;
}

export interface StudyInaccessibleResult {
  status: ProcedureRunStudyIdStatus.STUDY_INACCESSIBLE;
  procedureRunId: string;
  invalidStudyId: string;
}

export interface SuccessProcedureRunStudyIdResult {
  status: ProcedureRunStudyIdStatus.SUCCESS;
  studyId: string;
  procedureRunId: string;
}

export type ProcedureRunStudyIdResult =
  | SkippedProcedureRunStudyIdResult
  | PendingProcedureRunStudyIdResult
  | ErrorProcedureRunStudyIdResult
  | ProcedureRunInaccessibleResult
  | StudyInaccessibleResult
  | SuccessProcedureRunStudyIdResult;

export type NotSuccessfulProcedureRunStudyIdResult = Exclude<
  ProcedureRunStudyIdResult,
  SuccessProcedureRunStudyIdResult
>;

export function useProcedureRunStudyId(procedureRunId: string | null): ProcedureRunStudyIdResult {
  const queryResult = useSkippableBenchlingQuery<ProcedureRunStudyQuery, ProcedureRunStudyQueryVariables>(
    PROCEDURE_RUN_STUDY_QUERY,
    {
      variables: {procedureRunId: procedureRunId!},
      skip: procedureRunId == null,
    }
  );

  if (queryResult.type === QueryResultType.SKIPPED) {
    return {status: ProcedureRunStudyIdStatus.SKIPPED};
  }

  if (queryResult.type === QueryResultType.PENDING) {
    return {status: ProcedureRunStudyIdStatus.PENDING};
  }

  if (queryResult.type === QueryResultType.ERROR) {
    return {status: ProcedureRunStudyIdStatus.ERROR};
  }

  const procedureRun = queryResult.data.procedureRun;

  if (isInaccessible(procedureRun)) {
    return {
      status: ProcedureRunStudyIdStatus.PROCEDURE_RUN_INACCESSIBLE,
      invalidId: procedureRun.invalidId,
    };
  }

  const study = procedureRun.study;
  if (isInaccessible(study)) {
    return {
      status: ProcedureRunStudyIdStatus.STUDY_INACCESSIBLE,
      procedureRunId: procedureRun.id,
      invalidStudyId: study.invalidId,
    };
  }

  return {
    status: ProcedureRunStudyIdStatus.SUCCESS,
    studyId: study.id,
    procedureRunId: procedureRun.id,
  };
}
